<!-- src/components/ReuseMealPlanModal.vue -->
<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_MEALS_BY_DATE_RANGE, GET_MEAL_TYPES, GET_MEALS } from '../graphql/queries'
import { REUSE_MEAL_PLAN } from '../graphql/mutations'
import { showSuccess, parseGraphQLError } from '../utils/errorHandling'
import FormErrorSummary from './FormErrorSummary.vue'
import ReusePlanPreviewCalendar from './ReusePlanPreviewCalendar.vue'
import ReusePlanPreviewList from './ReusePlanPreviewList.vue'
import UnsavedChangesDialog from './UnsavedChangesDialog.vue'
import { vm } from '../utils/validationMessages'
import { keyToUtcIso } from '../utils/dateHelpers'
import { computeReusePreviewMeals } from '../utils/reuseMealPlanPreview'
import { useBodyScrollLock } from '../composables/useBodyScrollLock'
import dayjs from 'dayjs'
import type { Meal } from '../graphql/types'

interface Props {
  show: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  close: []
  success: [meals: Meal[]]
}>()

useBodyScrollLock(() => props.show)

const currentStep = ref(1)

// ── Step 1: source range + meal selection ──────────────────────────────────
const sourceStartDate = ref('') // YYYY-MM-DD (native date input)
const sourceEndDate = ref('') // YYYY-MM-DD (native date input)
const selectedMealIds = ref(new Set<string>())

const hasDateRange = computed(() => !!sourceStartDate.value && !!sourceEndDate.value)

const queryVariables = computed(() => ({
  start_date: dayjs(sourceStartDate.value, 'YYYY-MM-DD').format('DD/MM/YYYY'),
  end_date: dayjs(sourceEndDate.value, 'YYYY-MM-DD').format('DD/MM/YYYY'),
  show_active: false,
}))

const { result, loading } = useQuery(GET_MEALS_BY_DATE_RANGE, queryVariables, { enabled: hasDateRange })

const sourceMeals = computed<Meal[]>(() => result.value?.mealsByDateRange || [])

const { result: mealTypesResult } = useQuery(GET_MEAL_TYPES)

const mealTypeNamesById = computed<Record<string, string>>(() => {
  const map: Record<string, string> = {}
  for (const type of mealTypesResult.value?.mealTypes || []) {
    map[type.id] = type.name
  }
  return map
})

const getMealTypeName = (meal: Meal) => mealTypeNamesById.value[meal.type_id] || ''

// Same "is this a multi-day meal" check used by MealsTable.vue — reusing it
// shifts every one of its scheduled dates together, not just the primary one.
const isMultiDay = (meal: Meal) => (meal.portions_days ?? 1) > 1

// Default every meal in the range to selected whenever the source list changes.
watch(sourceMeals, (meals) => {
  selectedMealIds.value = new Set(meals.map((m) => m.id))
})

const toggleMeal = (id: string) => {
  const next = new Set(selectedMealIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selectedMealIds.value = next
}

const allSelected = computed(
  () => sourceMeals.value.length > 0 && sourceMeals.value.every((m) => selectedMealIds.value.has(m.id)),
)

const toggleSelectAll = () => {
  selectedMealIds.value = allSelected.value ? new Set() : new Set(sourceMeals.value.map((m) => m.id))
}

// Scroll-affordance for the meal list: shows a bottom fade while there's more
// content below the fold, hides it once scrolled (or scrollable) to the end.
const mealListRef = ref<HTMLElement | null>(null)
const hasMoreToScroll = ref(false)

const checkMealListOverflow = () => {
  const el = mealListRef.value
  hasMoreToScroll.value = !!el && el.scrollHeight - el.scrollTop - el.clientHeight > 1
}

watch(sourceMeals, () => nextTick(checkMealListOverflow))
onMounted(checkMealListOverflow)

const formatMealDate = (meal: Meal) => dayjs(meal.scheduled_date).format('ddd D MMM YYYY')

const canGoToStep2 = computed(() => selectedMealIds.value.size > 0)

// ── Validation ────────────────────────────────────────────────────────────────
const step1NextAttempted = ref(false)
const step2SubmitAttempted = ref(false)
const submitError = ref('')

const mealsErr = computed(() => (selectedMealIds.value.size === 0 ? vm.meals.required : ''))

const step1ErrorSummary = computed(() => {
  if (!step1NextAttempted.value || !mealsErr.value) return []
  return [{ label: 'Meals', message: mealsErr.value }]
})

// ── Step 2: target start date + submit ──────────────────────────────────────
const targetStartDate = ref('') // YYYY-MM-DD (native date input)

const targetDateErr = computed(() => (!targetStartDate.value ? vm.startDate.required : ''))
const canSubmit = computed(() => selectedMealIds.value.size > 0 && !!targetStartDate.value)

const selectedMeals = computed<Meal[]>(() => sourceMeals.value.filter((m) => selectedMealIds.value.has(m.id)))

const previewMeals = computed<Meal[]>(() => computeReusePreviewMeals(selectedMeals.value, targetStartDate.value))

// All meals (unfiltered) so the preview calendar can show existing meals
// alongside the reused ones — Apollo cache-shares this with MealsView.vue's
// identical query, so it costs no extra network round-trip in practice.
const { result: allMealsResult } = useQuery(GET_MEALS)
const existingMeals = computed<Meal[]>(() => allMealsResult.value?.meals || [])

const step2ErrorSummary = computed(() => {
  if (!step2SubmitAttempted.value || !targetDateErr.value) return []
  return [{ label: 'Target Date', message: targetDateErr.value }]
})

const submitErrorSummary = computed(() => (submitError.value ? [{ label: 'Error', message: submitError.value }] : []))

const { mutate: reuseMealPlan, loading: submitting } = useMutation(REUSE_MEAL_PLAN, {
  update(cache, { data }) {
    const createdMeals: Meal[] = data?.reuseMealPlan || []
    cache.modify({
      fields: {
        meals(existingMeals = []) {
          return [...existingMeals, ...createdMeals]
        },
      },
    })
  },
})

const nextStep = () => {
  if (currentStep.value !== 1) return
  step1NextAttempted.value = true
  if (canGoToStep2.value) currentStep.value = 2
}

const prevStep = () => {
  if (currentStep.value > 1) currentStep.value--
}

const submitReuse = async () => {
  step2SubmitAttempted.value = true
  submitError.value = ''
  if (!canSubmit.value) return
  try {
    const mealIds = Array.from(selectedMealIds.value)
    const result = await reuseMealPlan({
      mealIds,
      targetStartDate: keyToUtcIso(targetStartDate.value),
    })
    const createdMeals: Meal[] = result?.data?.reuseMealPlan || []
    showSuccess(`Reused ${createdMeals.length} meal${createdMeals.length !== 1 ? 's' : ''}!`)
    emit('success', createdMeals)
    closeModal()
  } catch (e) {
    submitError.value = parseGraphQLError(e).message
  }
}

const resetForm = () => {
  currentStep.value = 1
  sourceStartDate.value = ''
  sourceEndDate.value = ''
  selectedMealIds.value = new Set()
  targetStartDate.value = ''
  step1NextAttempted.value = false
  step2SubmitAttempted.value = false
  submitError.value = ''
  showUnsavedChangesDialog.value = false
}

const closeModal = () => {
  resetForm()
  emit('close')
}

// Any progress past the initial empty state is worth confirming before discarding.
const showUnsavedChangesDialog = ref(false)
const hasUnsavedChanges = computed(
  () => !!sourceStartDate.value || !!sourceEndDate.value || !!targetStartDate.value || currentStep.value > 1,
)

const attemptClose = () => {
  if (hasUnsavedChanges.value) {
    showUnsavedChangesDialog.value = true
  } else {
    closeModal()
  }
}

watch(
  () => props.show,
  (show) => {
    if (!show) resetForm()
  },
)
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="attemptClose">
      <div
        class="bg-white rounded-lg w-full max-w-[880px] flex flex-col overflow-hidden modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reuse-meal-plan-title"
      >
        <!-- ── Header ──────────────────────────────────────────────────────── -->
        <div class="flex-shrink-0 flex items-center justify-between px-6 modal-hd">
          <h2 id="reuse-meal-plan-title" class="font-bold text-[#0F172A] modal-title">Reuse Meal Plan</h2>
          <button @click="attemptClose" class="modal-close-button" aria-label="Close modal">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- ── Body ────────────────────────────────────────────────────────── -->
        <div class="flex-1 overflow-y-auto min-h-0 p-6 flex flex-col gap-6">
          <!-- ── STEP 1: Source range + meal selection ────────────────────── -->
          <template v-if="currentStep === 1">
          <FormErrorSummary :errors="step1ErrorSummary" />
          <div class="flex flex-col gap-3">
            <h3 class="font-bold text-[#0F172A] step-section-title">Source date range</h3>
            <p class="text-sm text-[#64748B]">Pick the range of meals you'd like to reuse.</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Start date</label>
                <input
                  type="date"
                  v-model="sourceStartDate"
                  class="form-field min-h-[44px] text-[16px] sm:text-base w-full"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">End date</label>
                <input
                  type="date"
                  v-model="sourceEndDate"
                  class="form-field min-h-[44px] text-[16px] sm:text-base w-full"
                />
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
              <h3 class="font-bold text-[#0F172A] step-section-title">Meals to reuse</h3>
              <div v-if="hasDateRange && sourceMeals.length" class="flex items-center gap-3">
                <span class="text-sm text-[#64748B]">
                  {{ selectedMealIds.size }} of {{ sourceMeals.length }} selected
                </span>
                <button type="button" @click="toggleSelectAll" class="select-all-btn">
                  {{ allSelected ? 'Unselect all' : 'Select all' }}
                </button>
              </div>
            </div>

            <div v-if="!hasDateRange" class="flex items-center gap-2 justify-center py-8 text-sm text-[#64748B]">
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
              </svg>
              Use the Start date and End date fields above to see the meals in that range.
            </div>
            <div v-else-if="loading" class="flex justify-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4F8646]"></div>
            </div>
            <div v-else-if="sourceMeals.length === 0" class="text-center py-8 text-sm text-[#64748B]">
              No meals found in that date range.
            </div>
            <div v-else class="relative">
              <div
                ref="mealListRef"
                @scroll="checkMealListOverflow"
                class="flex flex-col gap-2 max-h-72 overflow-y-auto"
              >
                <button
                  v-for="meal in sourceMeals"
                  :key="meal.id"
                  type="button"
                  @click="toggleMeal(meal.id)"
                  class="w-full flex items-center gap-3 rounded-lg px-4 py-3 text-left transition-all meal-option"
                  :class="{ 'meal-option--selected': selectedMealIds.has(meal.id) }"
                >
                  <span class="flex-1 min-w-0">
                    <span class="flex items-center gap-2 flex-wrap">
                      <span class="font-semibold text-[#0F172A] truncate">{{ meal.title }}</span>
                      <span v-if="getMealTypeName(meal)" class="category-badge category-main-dishes">{{ getMealTypeName(meal) }}</span>
                    </span>
                    <span class="block text-xs text-[#64748B] mt-0.5">{{ formatMealDate(meal) }}</span>
                    <div
                      v-if="isMultiDay(meal)"
                      class="mt-1 meal-portions-label"
                      :class="meal.portions_mode === 'repeat' ? 'meal-portions-label--repeat' : 'meal-portions-label--leftovers'"
                    >
                      <svg class="meal-portions-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          v-if="meal.portions_mode === 'repeat'"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                        />
                        <template v-else>
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z"
                          />
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z"
                          />
                        </template>
                      </svg>
                      <span>
                        {{ meal.portions_days }}d ·
                        {{ meal.portions_mode === 'repeat' ? 'Cook fresh each day' : 'Cooked Once' }}
                      </span>
                    </div>
                  </span>
                  <span class="flex-shrink-0 meal-checkbox-hit-area">
                    <span
                      class="w-5 h-5 rounded border-2 flex items-center justify-center transition-all meal-checkbox"
                      :class="{ 'meal-checkbox--selected': selectedMealIds.has(meal.id) }"
                    >
                      <svg v-if="selectedMealIds.has(meal.id)" class="w-3 h-3" fill="none" stroke="white" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  </span>
                </button>
              </div>
              <div v-if="hasMoreToScroll" class="meal-list-fade" aria-hidden="true"></div>
            </div>
          </div>
          </template>

          <!-- ── STEP 2: Target start date ─────────────────────────────────── -->
          <template v-else>
          <FormErrorSummary :errors="[...step2ErrorSummary, ...submitErrorSummary]" />
          <div class="flex flex-col gap-3">
            <h3 class="font-bold text-[#0F172A] step-section-title">Target start date</h3>
            <p class="text-sm text-[#64748B]">
              The earliest selected meal will land on this date; the rest shift by the same number of days.
              Pick a date above, or click any day on the calendar below.
            </p>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Start date</label>
              <input
                type="date"
                v-model="targetStartDate"
                :disabled="submitting"
                class="form-field min-h-[44px] text-[16px] sm:text-base w-full max-w-xs"
              />
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
              <h3 class="font-bold text-[#0F172A] step-section-title">Preview</h3>
              <span class="text-sm text-[#64748B]">
                {{ selectedMealIds.size }} meal{{ selectedMealIds.size !== 1 ? 's' : '' }} selected
              </span>
            </div>

            <ReusePlanPreviewCalendar
              class="hidden lg:block"
              :preview-meals="previewMeals"
              :existing-meals="existingMeals"
              :meal-type-names-by-id="mealTypeNamesById"
              :initial-month="targetStartDate || undefined"
              :selected-date="targetStartDate"
              @select-date="targetStartDate = $event"
            />
            <ReusePlanPreviewList
              class="lg:hidden"
              :preview-meals="previewMeals"
              :existing-meals="existingMeals"
              :meal-type-names-by-id="mealTypeNamesById"
            />
          </div>
          </template>
        </div>

        <!-- ── Footer ──────────────────────────────────────────────────────── -->
        <div class="flex-shrink-0 flex items-center justify-between modal-ft">
          <button
            v-if="currentStep === 1"
            @click="attemptClose"
            class="cancel-btn font-semibold transition-colors hover:opacity-80"
          >
            Cancel
          </button>
          <button
            v-else
            @click="prevStep"
            :disabled="submitting"
            class="flex items-center gap-2 rounded-lg font-bold transition-colors hover:opacity-90 back-btn disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <button
            v-if="currentStep === 1"
            @click="nextStep"
            class="rounded-lg font-bold text-white transition-all primary-action-btn"
            :class="!canGoToStep2 ? 'opacity-60' : 'hover:opacity-90'"
          >
            Next: Target Date
          </button>
          <button
            v-else
            @click="submitReuse"
            :disabled="submitting"
            class="rounded-lg font-bold text-white transition-all primary-action-btn disabled:opacity-50 disabled:cursor-not-allowed"
            :class="!canSubmit ? 'opacity-60' : 'hover:opacity-90'"
          >
            {{ submitting ? 'Reusing...' : 'Reuse Plan' }}
          </button>
        </div>
      </div>
    </div>

    <UnsavedChangesDialog
      :show="showUnsavedChangesDialog"
      @keep-editing="showUnsavedChangesDialog = false"
      @discard="closeModal"
    />
  </Teleport>
</template>

<style scoped>
.modal-panel {
  max-height: 90vh;
  box-shadow: 0px 25px 50px -12px rgba(0, 0, 0, 0.25);
}
.modal-hd {
  border-bottom: 1px solid #f1f5f9;
  min-height: 64px;
}
.modal-title {
  font-size: 20px;
}
.modal-ft {
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
  padding: 24px;
}
.step-section-title {
  font-size: 16px;
}
.meal-option {
  border: 1px solid #f1f5f9;
}
.meal-option--selected {
  background: rgba(79, 134, 70, 0.05);
  border-color: #4f8646;
}
.meal-checkbox {
  border-color: #e2e8f0;
  background: transparent;
}
.meal-checkbox--selected {
  border-color: #4f8646;
  background: #4f8646;
}
.meal-portions-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
  letter-spacing: 0.01em;
}
.meal-portions-label--leftovers {
  background: var(--color-primary-50, #edf3ec);
  color: var(--color-primary-700, #3f4a3c);
}
.meal-portions-label--repeat {
  background: #eff6ff;
  color: #1d4ed8;
}
.meal-portions-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}
.select-all-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  color: #4f8646;
  background: rgba(79, 134, 70, 0.08);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 150ms ease;
}
.select-all-btn:hover {
  background: rgba(79, 134, 70, 0.16);
}
.meal-checkbox-hit-area {
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.meal-list-fade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 32px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), white 90%);
  pointer-events: none;
  border-radius: 0 0 8px 8px;
}
.cancel-btn {
  font-size: 14px;
  color: #475569;
  padding: 10px 24px;
  background: none;
  border: none;
  cursor: pointer;
}
.back-btn {
  background: rgba(79, 134, 70, 0.2);
  color: #334155;
  padding: 10px 24px;
  font-size: 14px;
  border: none;
  cursor: pointer;
}
.primary-action-btn {
  background: #4f8646;
  padding: 10px 32px;
  font-size: 14px;
  box-shadow:
    0px 4px 6px -4px rgba(79, 134, 70, 0.3),
    0px 10px 15px -3px rgba(79, 134, 70, 0.3);
  border: none;
  cursor: pointer;
}
</style>
